import { updateState } from "config/utils"
import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { EmployeeAddressesService } from "services/employees"
import { EmployeeAddress } from "@type/employees.types"
import { AppThunk, RootState } from "store"

export interface EmployeeAddressesStateInterface {
	list: EmployeeAddress[]
	loading: boolean
	saving: boolean
}

const initialState: EmployeeAddressesStateInterface = {
	list: [],
	loading: false,
	saving: false,
}

const employeeAddressSlice = createSlice({
	name: "address",
	initialState,
	reducers: {
		fetchingEmployeeAddresses: state => {
			state.loading = true
		},
		fetchedEmployeeAddresses: (state, action: PayloadAction<EmployeeAddress[]>) => {
			state.list = action.payload
			state.loading = false
		},
		fetchingEmployeeAddressesFailed: state => {
			state.loading = false
		},
		savingEmployeeAddress: state => {
			state.saving = true
		},
		employeeAddressSaved: (state, action: PayloadAction<EmployeeAddress>) => {
			state.saving = false
			const [updatedValue] = updateState(state.list, action.payload, "_id")
			state.list = updatedValue
		},
		savingEmployeeAddressFailed: state => {
			state.saving = false
		},
	},
})

//REDUCER
export default employeeAddressSlice.reducer

//ACTIONS
export const {
	fetchingEmployeeAddresses,
	fetchedEmployeeAddresses,
	fetchingEmployeeAddressesFailed,
	employeeAddressSaved,
	savingEmployeeAddress,
	savingEmployeeAddressFailed,
} = employeeAddressSlice.actions

const getAllEmployeeAddresses =
	(id: string): AppThunk =>
	async dispatch => {
		try {
			dispatch(fetchingEmployeeAddresses())

			const { data: employeeAddresses } = await EmployeeAddressesService.getEmployeeAddresses(id)
			dispatch(fetchedEmployeeAddresses(employeeAddresses))
		} catch (error) {
			dispatch(fetchingEmployeeAddressesFailed())
		}
	}

const saveEmployeeAddress =
	(data: EmployeeAddress, cb?: () => void): AppThunk =>
	async dispatch => {
		try {
			dispatch(savingEmployeeAddress())
			let employeeAddressResponse = null
			if (data?._id) {
				const { _id, ...rest } = data
				employeeAddressResponse = (await EmployeeAddressesService.updateEmployeeAddress(_id, rest)).data
			} else {
				employeeAddressResponse = (await EmployeeAddressesService.createEmployeeAddress(data)).data
			}
			dispatch(employeeAddressSaved(employeeAddressResponse))
			cb?.()
		} catch (error) {
			dispatch(fetchingEmployeeAddressesFailed())
		}
	}

export { getAllEmployeeAddresses, saveEmployeeAddress }

//SELECTORS
const selectEmployeeAddressState = (state: RootState) => state.employee.address
const isEmployeeAddressesLoading = () => (state: RootState) => selectEmployeeAddressState(state).loading
const isEmployeeAddressesSaving = () => (state: RootState) => selectEmployeeAddressState(state).saving
const selectEmployeeAddressList = () => (state: RootState) => selectEmployeeAddressState(state)?.list ?? []

export { isEmployeeAddressesLoading, isEmployeeAddressesSaving, selectEmployeeAddressList }
